import {
  fetchCultureSelectorData,
  getSessionHandoffToken,
  persistUserSessionData,
  useCultureSelectorData,
} from '@skyscanner-internal/global-components/culture-selector/helpers';
import Header from '@skyscanner-internal/global-components/header';
import {
  CONFIG_SERVICE_KEYS,
  FEATURE_DECISION_KEYS,
} from '@web-platform/shared-universal-constants-config';
import {
  loggedInStatusChanged,
  loginVisibilityChanged,
} from '@web-platform/shared-universal-redux/src/actions/user';
import { getLogin, getTrustedFunnel } from '@web-platform/shared-universal-redux/src/state';
import { translate } from '@web-platform/shared-universal-translations';
import {
  getConfigServiceValue,
  getFeatureDecision,
} from '@web-platform/shared-universal-utils-frontend/src/config';
import { getAuthClient } from '@web-platform/shared-universal-utils-frontend/src/singletons/authClient';
import { getHeaderData } from '@web-platform/shared-universal-utils-frontend/src/singletons/globalHeaderData';
import { getSubscriptionClient } from '@web-platform/shared-universal-utils-frontend/src/singletons/subscriptionClient';
import { useDispatch, useSelector } from 'react-redux';
import { logError, logOperationalEvent, logWarn } from 'saddlebag-logger';
import { getProfilePicture, isLoggedIn } from 'saddlebag-user-management';

import type { Source } from '@skyscanner-internal/global-components/login-form';
import type { ScreenName } from '@web-platform/shared-universal-constants/src/screenName';
import type {
  ContextHeaderData,
  LoginFormStringOverrides,
} from '@web-platform/shared-universal-types/src/context';
import type { LoginModalReferrer } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/login';
import type { StateType } from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';

import STYLES from './GlobalHeader.module.scss';

const buildLoginFormProps = ({
  dispatch,
  headerProps,
  homepageDropsEnabled,
  isLoginModalOpen,
  loginModalSource,
  screenName,
}: {
  headerProps: ContextHeaderData & { isProductionEnv: boolean };
  homepageDropsEnabled: boolean | string | undefined;
  isLoginModalOpen: boolean;
  loginModalSource: LoginModalReferrer;
  screenName: ScreenName;
  dispatch: ReturnType<typeof useDispatch>;
}) => {
  if (!headerProps.loginFormProps) return undefined;

  const authClient = getAuthClient();
  const subscriptionClient = getSubscriptionClient();

  if (!subscriptionClient) return undefined;

  // Keyed on NonNullable<LoginModalReferrer> so TypeScript enforces exhaustive coverage —
  // adding a new referrer to LoginModalReferrer will cause a compile error here until it's mapped.
  const loginSourceMapping: Record<NonNullable<LoginModalReferrer>, Source> = {
    PRICE_ALERT: 'PRICE_ALERTS',
    SAVE_TO_LIST: 'S2L_ITINERARY',
    LOYALTY_POC: 'OTHER',
    LOYALTY_POC_DAY_VIEW: 'OTHER',
    HEADER_LOG_IN: 'HEADER',
    HEADER_SAVE_TO_LIST: 'S2L_HEADER_ICON',
    PRICING_OPTION_LOYALTY_CTA: 'OTHER',
    AUTO_PROMPT: 'AUTO_PROMPT',
    FLIGHT_ENTRY_LOYALTY_CTA: 'OTHER',
    DROPS_CTA: 'OTHER',
  };
  // null referrer means the modal was opened without a known source (e.g. direct header click)
  const source = loginModalSource ? loginSourceMapping[loginModalSource] : 'HEADER';

  const isLoyaltyPocLogin =
    loginModalSource === 'LOYALTY_POC' || loginModalSource === 'LOYALTY_POC_DAY_VIEW';

  // stringOverrides keys are camelCase (from global-components API), map LoginModalReferrer accordingly
  const referrerToOverrideKey: Partial<
    Record<NonNullable<LoginModalReferrer>, LoginFormStringOverrides>
  > = {
    SAVE_TO_LIST: 'saveToList',
    PRICE_ALERT: 'priceAlerts',
    LOYALTY_POC: 'loyaltyPoc',
    LOYALTY_POC_DAY_VIEW: 'loyaltyPoc',
  };
  const referrerLookupKey = isLoyaltyPocLogin ? 'LOYALTY_POC' : loginModalSource;
  const overrideKey: LoginFormStringOverrides | undefined = referrerLookupKey
    ? referrerToOverrideKey[referrerLookupKey]
    : undefined;

  const experimentContext = [];

  if (homepageDropsEnabled && loginModalSource === 'DROPS_CTA') {
    experimentContext.push('HOMEPAGE_DROPS');
  }

  return {
    ...headerProps.loginFormProps,
    authClient,
    experimentContext: experimentContext.join(','),
    isOpen: isLoginModalOpen,
    subscriptionClient,
    onVisibilityChange: ({
      loginFlowComplete,
      shouldBeOpen,
    }: {
      shouldBeOpen?: boolean;
      loginFlowComplete?: boolean;
    }) => {
      // if the login flow has been completed, retain the source in state so that contextual callback logic can use it - the login modal can't surface again
      // if the login flow has not been completed, clear the source so that any new login modal is operating from clean context
      if (loginFlowComplete) {
        dispatch(loggedInStatusChanged(isLoggedIn()));
        dispatch(loginVisibilityChanged(!!shouldBeOpen, loginModalSource));
      } else {
        dispatch(loginVisibilityChanged(!!shouldBeOpen));
      }
    },
    source,
    consumer: 'acorn',
    screenName,
    strings: {
      ...headerProps.loginFormProps.strings,
      ...(overrideKey ? headerProps.stringOverrides?.[overrideKey] : {}),
    },
  };
};

type Props = {
  primaryNavInFss?: boolean;
};

export const GlobalHeader = ({ primaryNavInFss = false }: Props) => {
  const dispatch = useDispatch();
  const isLoginModalOpen = useSelector((state: StateType) => getLogin(state).isOpen);
  const loginModalSource = useSelector((state: StateType) => getLogin(state).referrer);
  const currentScreenName = useSelector(
    (state: StateType) => getTrustedFunnel(state).currentScreenName,
  );
  const headerData = getHeaderData();
  const homepageDropsEnabled = getFeatureDecision(FEATURE_DECISION_KEYS.HOMEPAGE_DROPS_ENABLED);
  const isPackageExperimentEnabled = getFeatureDecision(
    FEATURE_DECISION_KEYS.PACKAGES_HEADER_TAB_ENABLED,
  );
  const isStaysHeaderTabBubbleEnabled = getFeatureDecision(
    FEATURE_DECISION_KEYS.HOTELS_HEADER_TAB_BUBBLE_ENABLED,
  );
  const packagesHeaderTabVersion = getConfigServiceValue(
    CONFIG_SERVICE_KEYS.EXPERIMENT.PACKAGES_HEADER_TAB_VERSION,
  );
  const staysStrings = getFeatureDecision(FEATURE_DECISION_KEYS.STAYS_COPY_HEADER_TAB_ENABLED);
  const locale = headerData?.locale || 'en-GB';
  const {
    currencies,
    enMarkets,
    isFetchingCultureSelectorData,
    locales,
    markets,
    onCultureSelectorButtonClick,
  } = useCultureSelectorData(fetchCultureSelectorData, locale, false);

  if (!headerData) {
    return null;
  }

  const headerProps = {
    ...headerData,
    isProductionEnv: headerData.isProductionEnv ?? false,
  };

  const enableAnonymousUtidPropagation = getFeatureDecision(
    FEATURE_DECISION_KEYS.ANONYMOUS_UTID_PROPAGATION_EXPLICIT_TLD_REDIRECT,
  );

  if (headerProps.cultureSelectorProps) {
    headerProps.cultureSelectorProps.currencies = currencies;
    headerProps.cultureSelectorProps.markets = markets;
    headerProps.cultureSelectorProps.locales = locales;
    headerProps.cultureSelectorProps.enMarkets = enMarkets;
    headerProps.cultureSelectorProps.persistUserSessionDataFn = enableAnonymousUtidPropagation
      ? persistUserSessionData
      : undefined;
    headerProps.cultureSelectorProps.getSessionHandoffTokenFn = getSessionHandoffToken;
  }

  const loginFormProps = buildLoginFormProps({
    headerProps,
    homepageDropsEnabled,
    isLoginModalOpen,
    loginModalSource,
    screenName: currentScreenName,
    dispatch,
  });

  return (
    <div id="header-root" className={STYLES.headerRootOverlay}>
      <Header
        {...headerProps}
        strings={{
          ...headerData.strings,
          carHire: translate('ktxtHeader_cars_label'),
          hotels: staysStrings ? translate('homepageTab_stays') : headerData.strings.hotels,
        }}
        onCultureSelectorButtonClick={onCultureSelectorButtonClick}
        isFetchingCultureSelectorData={isFetchingCultureSelectorData}
        logger={{
          logOperationalEvent,
          logError,
          logWarn,
        }}
        userLoggedIn={isLoggedIn()}
        getProfilePicture={getProfilePicture}
        loginFormProps={loginFormProps}
        showTabs={!primaryNavInFss}
        showPackagesTab={isPackageExperimentEnabled}
        unstable__hotelsNewBubble={Boolean(isStaysHeaderTabBubbleEnabled)}
        unstable__newBubble={
          ['NO_VISUAL_CHANGES_WITH_NEW_BUBBLE', 'VISUAL_CHANGE_AND_NEW_BUBBLE'].includes(
            packagesHeaderTabVersion,
          ) && isPackageExperimentEnabled
        }
        unstable__packagesEnableVisualChange={
          ['VISUAL_CHANGE_ONLY', 'VISUAL_CHANGE_AND_NEW_BUBBLE'].includes(
            packagesHeaderTabVersion,
          ) && isPackageExperimentEnabled
        }
      />
    </div>
  );
};
